// deps
import React, { memo, useCallback, useState } from "react";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";

// utils
import { getCloudinaryUrl, LottieAnimation } from "@swiggy-private/core-ui";

// styles
import { UpiStepWrapper, LottieContainer, ContentSection, UpiTextFieldSection } from "./upiStepStyles";
import { ButtonContainer } from "./styled";

// constants
import {
    SPLIT_BILL_LOTTIE_PROPS,
    UPI_STEP_HEADING,
    UPI_ID_LABEL,
    UPI_HINT_TEXT,
    SEND_REQUEST_BUTTON_TEXT,
    UPI_ID_FIELD_NAME,
    UPI_LOTTIE_PATH,
    UPI_ID_REGEX,
    UPI_ID_REQUIRED_ERROR,
    UPI_ID_INVALID_ERROR,
} from "@dineout-payment-success-ui/constants";

// types
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";

// hooks
import { useSplitBillAnalytics } from "@dineout-payment-success-ui/hooks/useSplitBillAnalytics";

// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface SplitBillUpiStepProps {
    onSendRequest: (upiId: string) => void;
    orderId?: string;
    totalBillAmount?: number;
    splitMethod?: string;
    restaurantId?: string;
}

const SplitBillUpiStep: React.FC<SplitBillUpiStepProps> = ({
    onSendRequest,
    orderId = "",
    totalBillAmount = 0,
    splitMethod = "",
    restaurantId = "",
}) => {
    const [upiId, setUpiId] = useState("");
    const [error, setError] = useState("");

    // Analytics hook
    const { onClickSplitUpiEntry } = useSplitBillAnalytics();

    const validateUpiId = useCallback((value: string): boolean => {
        const trimmed = value.trim();
        if (isEmpty(trimmed)) {
            setError(UPI_ID_REQUIRED_ERROR);
            return false;
        }
        if (!UPI_ID_REGEX.test(trimmed)) {
            setError(UPI_ID_INVALID_ERROR);
            return false;
        }
        setError("");
        return true;
    }, []);

    const handleUpiIdChange = (value: string) => {
        setUpiId(value);
        if (error) {
            setError("");
        }
    };

    const handleSendRequest = useCallback(() => {
        // Fire UPI entry click analytics
        onClickSplitUpiEntry({
            bookingId: orderId,
            totalBillAmount,
            splitMethod,
            upiSelected: upiId ? 1 : 0,
            restaurantId,
        });

        if (validateUpiId(upiId)) {
            onSendRequest(upiId.trim());
        }
    }, [
        onClickSplitUpiEntry,
        orderId,
        totalBillAmount,
        splitMethod,
        upiId,
        restaurantId,
        validateUpiId,
        onSendRequest,
    ]);

    return (
        <UpiStepWrapper data-testid={SPLIT_BILL_TEST_IDS.UPI_STEP}>
            <LottieContainer>
                <LottieAnimation
                    fileName={getCloudinaryUrl(UPI_LOTTIE_PATH)}
                    visible={true}
                    playLottie
                    {...SPLIT_BILL_LOTTIE_PROPS}
                />
            </LottieContainer>

            <ContentSection>
                <Text variant="V2.subheading1.neutral.bold" color="text_High_Emphasis">
                    {UPI_STEP_HEADING}
                </Text>

                <UpiTextFieldSection>
                    <TextField
                        textFieldDetails={{
                            value: upiId,
                            onChange: handleUpiIdChange,
                            type: TextFieldTypes.TEXT,
                            name: UPI_ID_FIELD_NAME,
                            label: UPI_ID_LABEL,
                            hintText: error ? "" : UPI_HINT_TEXT,
                            errorText: error,
                            inputStyle: {
                                backgroundColor: "white",
                            },
                            attrs: {
                                required: true,
                                "aria-required": true,
                                "data-testid": SPLIT_BILL_TEST_IDS.UPI_INPUT,
                            },
                        }}
                    />
                </UpiTextFieldSection>
            </ContentSection>

            <ButtonContainer>
                <FullWidthButton
                    variant="primary"
                    type="button"
                    onClick={handleSendRequest}
                    data-testid={SPLIT_BILL_TEST_IDS.SEND_REQUEST}
                >
                    {SEND_REQUEST_BUTTON_TEXT}
                </FullWidthButton>
            </ButtonContainer>
        </UpiStepWrapper>
    );
};

export default memo(SplitBillUpiStep);
